import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useUI } from '../context/UIContext';
import { useProfile } from '../context/ProfileContext';
import TradeCardTile from '../components/fairtrade/TradeCardTile';
import TradePricingOverlay from '../components/fairtrade/TradePricingOverlay';
import { fetchPricing, fetchGradedPricing } from '../lib/fetchPricing';
import { isGradedCard } from '../utils/poketraceService';
import { upsertCardPrice } from '../services/cardPriceCache';

const SESSION_KEY = 'fairTradeSession';

// Map card condition to JustTCG key (raw cards only)
const conditionToKey = (condition) => {
  if (!condition) return 'nm';
  const upper = condition.toUpperCase();
  if (upper === 'NM') return 'nm';
  if (upper === 'LP') return 'lp';
  if (upper === 'MP') return 'mp';
  if (upper === 'HP') return 'hp';
  if (upper === 'DMG') return 'dmg';
  return 'nm';
};

const FairTrade = () => {
  const { theme } = useTheme();
  const { setHideNav } = useUI();
  const { userProfile, activeProfile } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const pricingEnabled = activeProfile?.pricing_trigger && activeProfile?.pricing_enabled === true;
  const processedBatchRef = useRef(null);

  // Pricing detail overlay
  const [detailCard, setDetailCard] = useState(null);

  useEffect(() => {
    if (activeProfile && !pricingEnabled) {
      navigate('/', { replace: true });
    }
  }, [activeProfile, pricingEnabled, navigate]);

  // Restore session from sessionStorage
  const [myCards, setMyCards] = useState(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.myCards || [];
      }
    } catch (e) { /* ignore */ }
    return [];
  });

  const [theirCards, setTheirCards] = useState(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.theirCards || [];
      }
    } catch (e) { /* ignore */ }
    return [];
  });

  // Hide bottom nav while in Fair Trade
  useEffect(() => {
    setHideNav(true);
    return () => setHideNav(false);
  }, [setHideNav]);

  // Persist session on every change (strip base64 scan photos to stay under quota)
  useEffect(() => {
    try {
      const strip = (cards) => cards.map(({ userPhoto, ...rest }) => rest);
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ myCards: strip(myCards), theirCards: strip(theirCards) }));
    } catch (e) {
      // QuotaExceededError - session restore is nice-to-have, not critical
    }
  }, [myCards, theirCards]);

  // Handle incoming cards from BulkScanner trade mode
  useEffect(() => {
    const tradeCards = location.state?.tradeCards;
    const tradeSide = location.state?.tradeSide;
    if (!tradeCards || !Array.isArray(tradeCards) || tradeCards.length === 0) return;

    // Deduplicate: use first card's id as batch key
    const batchKey = tradeCards.map(c => c.id).join(',');
    if (batchKey === processedBatchRef.current) return;
    processedBatchRef.current = batchKey;

    // Add cards to the appropriate side
    if (tradeSide === 'theirs') {
      setTheirCards(prev => [...prev, ...tradeCards]);
    } else {
      setMyCards(prev => [...prev, ...tradeCards]);
    }

    // Clear location state
    navigate(location.pathname, { replace: true, state: {} });

    // Fetch pricing for each card (graded -> Poketrace, raw -> JustTCG)
    tradeCards.forEach(async (card) => {
      const cardId = card.id;
      try {
        let price = null;
        let pricingData = null;

        if (isGradedCard(card.condition)) {
          // Graded: use Poketrace
          const result = await fetchGradedPricing({
            name: card.name,
            setName: card.setName,
            number: card.number,
            condition: card.condition,
            variant: card.variant,
          });
          price = result.price;
          pricingData = result.pricingData;
        } else {
          // Raw: use JustTCG
          pricingData = await fetchPricing({
            name: card.name,
            setName: card.setName,
            localId: card.number,
          });
          if (pricingData?.conditions) {
            const key = conditionToKey(card.condition);
            price = pricingData.conditions[key]?.price || pricingData.conditions.nm?.price || null;
          }
        }

        const priceUpdate = (prev) => prev.map(c =>
          c.id === cardId ? {
            ...c,
            price,
            priceLoading: false,
            pricingData,
          } : c
        );
        setMyCards(priceUpdate);
        setTheirCards(priceUpdate);

        // Write to card_prices cache (non-blocking)
        if (pricingData) {
          upsertCardPrice({
            cardName: card.name,
            setName: card.setName || '',
            setNumber: card.number || '',
            ...(isGradedCard(card.condition)
              ? { gradedPrices: pricingData.gradedTiers || null, gradedFullData: pricingData }
              : { rawPrices: pricingData.conditions || null, rawFullData: pricingData, justtcgVariant: pricingData.printing || null }),
          }).catch(() => {});
        }
      } catch (err) {
        console.error('[FairTrade] Pricing error for', card.name, err);
        const errorUpdate = (prev) => prev.map(c =>
          c.id === cardId ? { ...c, priceLoading: false } : c
        );
        setMyCards(errorUpdate);
        setTheirCards(errorUpdate);
      }
    });
  }, [location.state?.tradeCards]);

  // Totals
  const myTotal = useMemo(() =>
    myCards.reduce((sum, c) => sum + (c.price || 0), 0),
    [myCards]
  );
  const theirTotal = useMemo(() =>
    theirCards.reduce((sum, c) => sum + (c.price || 0), 0),
    [theirCards]
  );
  const difference = myTotal - theirTotal;
  const maxTotal = Math.max(myTotal, theirTotal, 1);
  const diffPercent = Math.abs(difference) / maxTotal * 100;

  // Balance status
  const getBalanceStatus = () => {
    if (myCards.length === 0 && theirCards.length === 0) return { color: theme.textSecondary, bg: theme.bgCard, borderColor: theme.border, label: 'Add cards to compare' };
    if (diffPercent <= 10) return { color: theme.success, bg: theme.successBg, borderColor: theme.success, label: 'Fair Trade' };
    if (diffPercent <= 25) return { color: theme.warning, bg: theme.warningBg, borderColor: theme.warning, label: 'Close' };
    return { color: theme.error, bg: theme.errorBg, borderColor: theme.error, label: 'Uneven' };
  };
  const balance = getBalanceStatus();

  const handleRemoveCard = useCallback((side, cardId) => {
    if (side === 'mine') {
      setMyCards(prev => prev.filter(c => c.id !== cardId));
    } else {
      setTheirCards(prev => prev.filter(c => c.id !== cardId));
    }
  }, []);

  const handleReset = () => {
    setMyCards([]);
    setTheirCards([]);
    sessionStorage.removeItem(SESSION_KEY);
  };

  const handleClose = () => {
    navigate(-1);
  };

  return (
    <div style={{ ...styles.page, backgroundColor: theme.bg }}>
      {/* Header */}
      <div style={{ ...styles.header, borderBottom: `1px solid ${theme.border}` }}>
        <button style={{ ...styles.headerBtn, color: theme.text }} onClick={handleClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
        </button>
        <h1 style={{ ...styles.title, color: theme.text }}>Fair Trade</h1>
        <button
          style={{ ...styles.headerBtn, color: theme.error, opacity: (myCards.length + theirCards.length) > 0 ? 1 : 0.3 }}
          onClick={handleReset}
          disabled={(myCards.length + theirCards.length) === 0}
        >
          Reset
        </button>
      </div>

      {/* Scrollable content */}
      <div style={styles.content}>
        {/* My Cards Section */}
        <TradeSide
          label="My Cards"
          cards={myCards}
          total={myTotal}
          totalCount={myCards.length}
          theme={theme}
          onAdd={() => navigate('/bulk-scanner?mode=trade&side=mine')}
          onRemove={(id) => handleRemoveCard('mine', id)}
          onTapCard={setDetailCard}
        />

        {/* Balance Bar */}
        <div style={{ ...styles.balanceBar, backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }}>
          <div style={styles.balanceSide}>
            <span style={{ ...styles.balanceLabel, color: theme.textSecondary }}>Mine</span>
            <span style={{ ...styles.balanceAmount, color: theme.text }}>
              {myTotal > 0 ? `$${myTotal.toFixed(2)}` : '$0'}
            </span>
          </div>

          <div style={styles.balanceCenter}>
            <div style={{
              ...styles.balancePill,
              backgroundColor: balance.bg,
              border: `1px solid ${balance.borderColor}`,
            }}>
              <span style={{ ...styles.balanceStatus, color: balance.color }}>
                {balance.label}
              </span>
              {(myCards.length > 0 || theirCards.length > 0) && (
                <span style={{ ...styles.balanceDiff, color: balance.color }}>
                  {difference > 0 ? '+' : ''}{difference !== 0 ? `$${Math.abs(difference).toFixed(2)}` : 'Even'}
                </span>
              )}
            </div>
          </div>

          <div style={{ ...styles.balanceSide, alignItems: 'flex-end' }}>
            <span style={{ ...styles.balanceLabel, color: theme.textSecondary }}>Partner</span>
            <span style={{ ...styles.balanceAmount, color: theme.text }}>
              {theirTotal > 0 ? `$${theirTotal.toFixed(2)}` : '$0'}
            </span>
          </div>
        </div>

        {/* Trade Partner Section */}
        <TradeSide
          label="Trade Partner"
          cards={theirCards}
          total={theirTotal}
          totalCount={theirCards.length}
          theme={theme}
          onAdd={() => navigate('/bulk-scanner?mode=trade&side=theirs')}
          onRemove={(id) => handleRemoveCard('theirs', id)}
          onTapCard={setDetailCard}
        />
      </div>

      {/* Pricing Detail Overlay */}
      <AnimatePresence>
        {detailCard && (
          <TradePricingOverlay
            card={detailCard}
            onClose={() => setDetailCard(null)}
            cardDbId={detailCard?.id}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Trade side component
const TradeSide = ({ label, cards, total, totalCount, theme, onAdd, onRemove, onTapCard }) => {
  return (
    <div style={styles.sideSection}>
      <div style={styles.sideHeader}>
        <span style={{ ...styles.sideLabel, color: theme.text }}>{label}</span>
        <span style={{ ...styles.sideCount, color: theme.textSecondary }}>
          {totalCount > 0 ? `${totalCount} card${totalCount !== 1 ? 's' : ''}` : ''}
        </span>
      </div>

      {/* Card list */}
      <div style={styles.cardList}>
        {cards.map(card => (
          <TradeCardTile
            key={card.id}
            card={card}
            onRemove={() => onRemove(card.id)}
            onTap={() => card.pricingData ? onTapCard(card) : null}
          />
        ))}
      </div>

      {/* Empty state */}
      {cards.length === 0 && (
        <div style={{ ...styles.emptyState, color: theme.textSecondary }}>
          No cards yet
        </div>
      )}

      {/* Add card button */}
      <button
        style={{
          ...styles.addBtn,
          backgroundColor: theme.bgCard,
          border: `1px dashed ${theme.border}`,
          color: theme.accent,
        }}
        onClick={onAdd}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
        Add Card
      </button>
    </div>
  );
};

const styles = {
  page: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'max(12px, env(safe-area-inset-top)) 16px 12px',
    flexShrink: 0,
  },
  headerBtn: {
    background: 'none',
    border: 'none',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    margin: 0,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 16px max(16px, env(safe-area-inset-bottom))',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  sideSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  sideHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  sideLabel: {
    fontSize: 16,
    fontWeight: 700,
  },
  sideCount: {
    fontSize: 12,
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  emptyState: {
    textAlign: 'center',
    padding: '16px 0',
    fontSize: 13,
  },
  addBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '12px 16px',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  balanceBar: {
    display: 'flex',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    gap: 8,
  },
  balanceSide: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  balanceLabel: {
    fontSize: 11,
    fontWeight: 500,
  },
  balanceAmount: {
    fontSize: 18,
    fontWeight: 700,
  },
  balanceCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  balancePill: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '6px 12px',
    borderRadius: 8,
    gap: 1,
  },
  balanceStatus: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  balanceDiff: {
    fontSize: 13,
    fontWeight: 600,
  },
};

export default FairTrade;
